import React from "react";
import { Card, Button, Typography, Space, Statistic, Row, Col, Tag, Descriptions, Alert, Spin, Breadcrumb } from "antd";
import { ArrowLeftOutlined, BarChartOutlined, EyeOutlined, LinkOutlined, UserOutlined, BugOutlined, ExportOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import { useAuth } from "../../hooks/useAuth";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useGetAdminSurveysQuery } from "../../services/nestjs-base-api";
import { Utils } from "../../utils/utils";
import { BrandEquityComparison } from "../components/response-integrity/BrandEquityComparison";
import { CohortComparison } from "../components/response-integrity/CohortComparison";
import { SurveyIntegritySettings } from "../components/response-integrity/SurveyIntegritySettings";

const { Title, Text, Paragraph } = Typography;

export const AdminSurveyDetailsPage: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { surveyHash } = useParams<{ surveyHash: string }>();

    // Fetch all surveys to find the one with matching hash
    const { data: surveysData, isLoading, error } = useGetAdminSurveysQuery({
        jwtToken: user?.jwtToken!,
        limit: 1000, // Get all surveys to find the one by hash
    }, {
        skip: !user?.jwtToken
    });

    const survey = surveysData?.data?.find(s => s.hash === surveyHash);

    const getStatusColor = (status: string): string => {
        switch (status) {
            case 'active': return 'green';
            case 'draft': return 'default';
            case 'paused': return 'orange';
            case 'completed': return 'blue';
            default: return 'default';
        }
    };

    const handleBack = () => {
        navigate('/admin/surveys');
    };

    const handleViewResults = () => {
        navigate(`/admin/surveys/${surveyHash}/results`);
    };

    const handleImpersonateOrg = () => {
        // Navigate to the organization page to impersonate
        navigate(`/admin/organizations`);
    };

    const handleCopySurveyUrl = () => {
        if (survey) {
            const url = Utils.getPublicSurveyUrl(survey.hash);
            navigator.clipboard.writeText(url);
        }
    };

    const handleCopyTestUrl = () => {
        if (survey) {
            const testUrl = Utils.getTestSurveyUrl(survey.hash);
            navigator.clipboard.writeText(testUrl);
        }
    };

    const handleOpenTestUrl = () => {
        if (survey) {
            const testUrl = Utils.getTestSurveyUrl(survey.hash);
            window.open(testUrl, '_blank');
        }
    };

    if (isLoading) {
        return (
            <div style={{ padding: '24px', textAlign: 'center' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (error || !survey) {
        return (
            <div style={{ padding: '24px' }}>
                <Alert
                    message="Survey Not Found"
                    description="The survey you're looking for doesn't exist or has been removed."
                    type="error"
                    showIcon
                    action={
                        <Button onClick={handleBack}>
                            Back to Surveys
                        </Button>
                    }
                />
            </div>
        );
    }

    const publicUrl = Utils.getPublicSurveyUrl(survey.hash);
    const testUrl = Utils.getTestSurveyUrl(survey.hash);

    return (
        <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
            {/* Breadcrumb */}
            <Breadcrumb style={{ marginBottom: '24px' }}>
                <Breadcrumb.Item>
                    <Button 
                        type="link" 
                        onClick={() => navigate('/admin/dashboard')} 
                        style={{ padding: 0, height: 'auto', lineHeight: 'inherit' }}
                    >
                        Admin
                    </Button>
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                    <Button 
                        type="link" 
                        onClick={handleBack} 
                        style={{ padding: 0, height: 'auto', lineHeight: 'inherit' }}
                    >
                        Surveys
                    </Button>
                </Breadcrumb.Item>
                <Breadcrumb.Item>{survey.name}{survey.cohortName ? ` - ${survey.cohortName}` : ''}</Breadcrumb.Item>
            </Breadcrumb>

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
                <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
                        <Button
                            icon={<ArrowLeftOutlined />}
                            onClick={handleBack}
                        >
                            Back to Surveys
                        </Button>
                        <Tag color={getStatusColor(survey.status)} style={{ fontSize: '14px' }}>
                            {survey.status.toUpperCase()}
                        </Tag>
                    </div>
                    <Title level={2} style={{ margin: 0, marginBottom: 8 }}>
                        {survey.name}{survey.cohortName ? ` - ${survey.cohortName}` : ''}
                    </Title>
                    <Text type="secondary" style={{ fontSize: '16px' }}>
                        {survey.brandName} • {survey.organisationName}
                    </Text>
                </div>

                <Space>
                    <Button 
                        icon={<UserOutlined />}
                        onClick={handleImpersonateOrg}
                    >
                        View Organization
                    </Button>
                    {(survey.status === 'active' || survey.status === 'completed') && (
                        <Button type="primary" icon={<BarChartOutlined />} onClick={handleViewResults}>
                            View Results
                        </Button>
                    )}
                    <Link to={`/admin/response-integrity?surveyId=${survey.id}`}>
                        <Button icon={<SafetyCertificateOutlined />}>
                            Integrity
                        </Button>
                    </Link>
                </Space>
            </div>

            <Row gutter={[24, 24]}>
                {/* Survey Details */}
                <Col xs={24} lg={16}>
                    <Card title="Survey Information" style={{ marginBottom: '24px' }}>
                        <Descriptions column={2} layout="vertical">
                            <Descriptions.Item label="Organization">
                                <Button 
                                    type="link" 
                                    style={{ padding: 0, height: 'auto' }}
                                    onClick={() => navigate(`/admin/organizations`)}
                                >
                                    {survey.organisationName}
                                </Button>
                            </Descriptions.Item>
                            <Descriptions.Item label="Brand">
                                {survey.brandName}
                            </Descriptions.Item>
                            <Descriptions.Item label="Status">
                                <Tag color={getStatusColor(survey.status)}>
                                    {survey.status.toUpperCase()}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Created">
                                {new Date(survey.createdAt).toLocaleDateString()}
                            </Descriptions.Item>
                            {survey.cohortName && (
                                <Descriptions.Item label="Cohort">
                                    <Tag color={survey.cohortName === 'All Customers' ? 'green' : 'blue'}>
                                        {survey.cohortName}
                                    </Tag>
                                </Descriptions.Item>
                            )}
                        </Descriptions>
                        
                        {survey.description && (
                            <div style={{ marginTop: '16px' }}>
                                <Text strong>Description:</Text>
                                <Paragraph style={{ marginTop: '8px' }}>
                                    {survey.description}
                                </Paragraph>
                            </div>
                        )}
                    </Card>

                    {/* Public URL */}
                    <Card title="Public Survey URL">
                        <div style={{ marginBottom: 16 }}>
                            <Paragraph style={{ marginBottom: 12, color: '#6b7280' }}>
                                This is the public URL that participants use to access the survey.
                            </Paragraph>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 12, backgroundColor: '#f8fafc', borderRadius: 8 }}>
                                <LinkOutlined style={{ color: '#6b7280' }} />
                                <Text code style={{ flex: 1, margin: 0 }}>
                                    {publicUrl}
                                </Text>
                                <Button size="small" onClick={handleCopySurveyUrl}>
                                    Copy
                                </Button>
                            </div>
                        </div>
                        <Alert
                            type="info"
                            showIcon
                            message="Admin Access"
                            description="As an admin, you can view this survey's public URL and access its results, but you cannot modify the survey directly. Use the organization impersonation feature to make changes."
                        />
                    </Card>

                    <div style={{ marginBottom: 16 }}></div>

                    {/* Test URL */}
                    <Card title="Test Survey URL" style={{ marginBottom: '24px', backgroundColor: '#fff7e6', borderColor: '#ffa940' }}>
                        <div style={{ marginBottom: 16 }}>
                            <Paragraph style={{ marginBottom: 12, color: '#6b7280' }}>
                                Use this URL to test the survey without recording responses. Perfect for validating the survey experience.
                            </Paragraph>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 12, backgroundColor: '#f8fafc', borderRadius: 8 }}>
                                <BugOutlined style={{ color: '#6b7280' }} />
                                <Text code style={{ flex: 1, margin: 0 }}>
                                    {testUrl}
                                </Text>
                                <Space>
                                    <Button size="small" onClick={handleCopyTestUrl}>
                                        Copy Test URL
                                    </Button>
                                    <Button size="small" icon={<ExportOutlined />} onClick={handleOpenTestUrl}>
                                        Open Test Survey
                                    </Button>
                                </Space>
                            </div>
                        </div>
                        <Alert
                            type="warning"
                            showIcon
                            message="Testing Only - Do Not Share With Participants"
                            description="This test URL is for internal testing purposes only. Responses submitted via this link will not be saved or appear in survey results."
                        />
                    </Card>
                </Col>

                {/* Statistics */}
                <Col xs={24} lg={8}>
                    <Card title="Survey Statistics" style={{ marginBottom: '24px' }}>
                        <Row gutter={16}>
                            <Col span={24} style={{ marginBottom: '16px' }}>
                                <Statistic
                                    title="Total Responses"
                                    value={survey.responseCount}
                                    suffix="responses"
                                />
                            </Col>
                            <Col span={24} style={{ marginBottom: '16px' }}>
                                <Statistic
                                    title="Link Accesses"
                                    value={survey.accessCount}
                                    suffix="accesses"
                                />
                            </Col>
                            <Col span={24} style={{ marginBottom: '16px' }}>
                                <Statistic
                                    title="Completion Rate"
                                    value={survey.completionRate}
                                    precision={1}
                                    suffix="%"
                                />
                            </Col>
                            {survey.averageCompletionTime > 0 && (
                                <Col span={24}>
                                    <Statistic
                                        title="Avg. Completion Time"
                                        value={Math.round(survey.averageCompletionTime / 60)}
                                        suffix="minutes"
                                    />
                                </Col>
                            )}
                        </Row>
                    </Card>

                    {/* Admin Actions */}
                    <Card title="Admin Actions">
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Button 
                                block 
                                icon={<UserOutlined />}
                                onClick={handleImpersonateOrg}
                            >
                                View Organization
                            </Button>
                            {(survey.status === 'active' || survey.status === 'completed') && (
                                <Button 
                                    block 
                                    type="primary" 
                                    icon={<BarChartOutlined />} 
                                    onClick={handleViewResults}
                                >
                                    View Survey Results
                                </Button>
                            )}
                            <Button 
                                block 
                                icon={<EyeOutlined />}
                                onClick={() => window.open(testUrl, '_blank')}
                            >
                                Preview Survey
                            </Button>
                        </Space>
                    </Card>
                </Col>
            </Row>

            {survey.brandId && (
                <div style={{ marginTop: 24 }}>
                    <BrandEquityComparison brandId={survey.brandId} />
                </div>
            )}

            {survey.brandId && (
                <div style={{ marginTop: 24 }}>
                    <CohortComparison brandId={survey.brandId} />
                </div>
            )}

            <div style={{ marginTop: 24 }}>
                <SurveyIntegritySettings
                    surveyId={survey.id}
                    initialPanelPartner={survey.panelPartner ?? null}
                    initialDetectorProfile={survey.detectorProfile ?? null}
                    initialLlmBudgetOverrideUsd={
                        survey.llmBudgetOverrideUsd ?? null
                    }
                />
            </div>
        </div>
    );
};